import { FiLink } from 'react-icons/fi';

export const subjectVerbAgreementData = {
  courseId: 'subject-verb-agreement',
  courseType: 'grammar',
  title: 'Subject-Verb Agreement',
  icon: FiLink,
  overview: {
    impact: 'Critical for grammatical accuracy in all IELTS tasks',
    focus: 'Matching subjects with correct verb forms',
    uses: 'Writing Task 1 & 2, Speaking',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Basic Agreement Rules',
      content: `<p>The subject and verb in a sentence must agree in number. Singular subjects take singular verbs; plural subjects take plural verbs.</p>
      <h4>Remember:</h4>
      <p>In the present tense, singular verbs typically end in -s/es (runs, watches), while plural verbs don't (run, watch).</p>`,
      examples: [
        { label: 'Singular', text: 'The student studies hard.' },
        { label: 'Plural', text: 'The students study hard.' },
        { label: 'Singular', text: 'Technology is changing rapidly.' },
        { label: 'Plural', text: 'Technologies are changing rapidly.' },
      ],
    },
    {
      title: 'Tricky Subjects',
      content: `<p>Some subjects can be confusing. Here are the key rules:</p>`,
      table: {
        headers: ['Subject Type', 'Rule', 'Example'],
        rows: [
          ['Collective nouns', 'Usually singular', 'The team is winning.'],
          ['Uncountable nouns', 'Always singular', 'Information is valuable.'],
          ['Everyone/Nobody', 'Always singular', 'Everyone has an opinion.'],
          ['News/Mathematics', 'Singular despite -s ending', 'The news is concerning.'],
          ['None', 'Can be singular or plural', 'None of the cake is left. / None of the cakes are left.'],
        ],
      },
    },
    {
      title: 'Compound Subjects',
      content: `<p>When subjects are joined by conjunctions, agreement depends on the conjunction used:</p>`,
      points: [
        'Subjects joined by "and" are usually plural: "The cat and dog are playing."',
        'Subjects joined by "or/nor" take the verb that agrees with the closer subject: "Neither the students nor the teacher was happy."',
        'Phrases like "along with", "as well as", "together with" don\'t change the verb: "The teacher, along with her students, is traveling."',
      ],
    },
    {
      title: 'Distance Between Subject and Verb',
      content: `<p>When other words come between the subject and verb, find the true subject:</p>`,
      examples: [
        { 
          label: 'Correct', 
          text: 'The quality of the products has improved.',
          translation: 'Subject is "quality" (singular), not "products"'
        },
        { 
          label: 'Correct', 
          text: 'The students in my class are motivated.',
          translation: 'Subject is "students" (plural), not "class"'
        },
      ],
      warning: 'Don\'t be distracted by words between the subject and verb. Identify the true subject!',
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Verb',
      type: 'select',
      instructions: 'Select the verb form that agrees with the subject.',
      questions: [
        {
          text: 'The number of international students ___ increasing.',
          options: ['is', 'are', 'were'],
          answer: 'is',
          explanation: '"The number of" is singular, so use "is".',
        },
        {
          text: 'A variety of activities ___ available.',
          options: ['is', 'are', 'was'],
          answer: 'are',
          explanation: '"A variety of" typically takes a plural verb when referring to countable items.',
        },
        {
          text: 'Neither the manager nor the employees ___ satisfied.',
          options: ['was', 'were', 'is'],
          answer: 'were',
          explanation: 'With "neither...nor", the verb agrees with the closer subject (employees = plural).',
        },
        {
          text: 'The team ___ celebrating their victory.',
          options: ['is', 'are', 'were'],
          answer: 'is',
          explanation: 'In British English, collective nouns can take plural verbs, but "is" is also correct.',
        },
        {
          text: 'Each of the students ___ submitted their assignment.',
          options: ['has', 'have', 'were'],
          answer: 'has',
          explanation: '"Each" is always singular, so use "has".',
        },
        {
          text: 'The news about the economy ___ concerning.',
          options: ['is', 'are', 'were'],
          answer: 'is',
          explanation: '"News" is singular despite ending in -s.',
        },
        {
          text: 'A number of issues ___ raised during the meeting.',
          options: ['were', 'was', 'is'],
          answer: 'were',
          explanation: '"A number of" takes a plural verb (different from "the number of").',
        },
      ],
    },
    {
      title: 'Identify the True Subject',
      type: 'select',
      instructions: 'Identify the subject of the sentence.',
      questions: [
        {
          text: '"The quality of the products has improved."',
          options: ['quality', 'products', 'improved'],
          answer: 'quality',
          explanation: '"Quality" (singular) is the subject, not "products".',
        },
        {
          text: '"The students in my class are motivated."',
          options: ['students', 'class', 'motivated'],
          answer: 'students',
          explanation: '"Students" (plural) is the subject, not "class".',
        },
        {
          text: '"The impact of the new policies remains unclear."',
          options: ['impact', 'policies', 'remains'],
          answer: 'impact',
          explanation: '"Impact" (singular) is the subject. "Policies" is in a prepositional phrase.',
        },
        {
          text: '"The majority of voters support the candidate."',
          options: ['majority', 'voters', 'candidate'],
          answer: 'majority',
          explanation: '"Majority" is the subject (takes plural verb when followed by plural noun).',
        },
      ],
    },
    {
      title: 'Correct the Errors',
      type: 'fill-in',
      instructions: 'Find and correct the subject-verb agreement error.',
      questions: [
        {
          text: 'Error: "The results of the survey was surprising."',
          answer: 'The results of the survey were surprising.',
          explanation: '"Results" is the subject (plural), not "survey".',
        },
        {
          text: 'Error: "Everyone in the class have finished."',
          answer: 'Everyone in the class has finished.',
          explanation: '"Everyone" is always singular.',
        },
        {
          text: 'Error: "Mathematics are my favorite subject."',
          answer: 'Mathematics is my favorite subject.',
          explanation: '"Mathematics" is singular despite ending in -s.',
        },
        {
          text: 'Error: "The committee have made their decision."',
          answer: 'The committee has made its decision.',
          explanation: 'In American English, collective nouns take singular verbs.',
        },
        {
          text: 'Error: "Neither my brother nor my sisters was at home."',
          answer: 'Neither my brother nor my sisters were at home.',
          explanation: 'Verb agrees with the closer subject "sisters" (plural).',
        },
      ],
    },
    {
      title: 'Singular or Plural Verb?',
      type: 'select',
      instructions: 'Should the verb be singular or plural?',
      questions: [
        {
          text: 'The police ___ investigating the case.',
          options: ['are', 'is', 'was'],
          answer: 'are',
          explanation: '"Police" is a plural noun (no singular form in this sense).',
        },
        {
          text: 'Two miles ___ a long distance to walk.',
          options: ['is', 'are', 'were'],
          answer: 'is',
          explanation: 'Distances are treated as singular units.',
        },
        {
          text: 'All of the information ___ accurate.',
          options: ['is', 'are', 'were'],
          answer: 'is',
          explanation: '"Information" is uncountable, so use singular verb.',
        },
        {
          text: 'Half of the students ___ absent.',
          options: ['were', 'was', 'is'],
          answer: 'were',
          explanation: '"Students" is countable and plural, so "half" takes plural verb.',
        },
      ],
    },
    {
      title: 'Complete with Correct Verb',
      type: 'writing',
      instructions: 'Write sentences using the correct subject-verb agreement.',
      questions: [
        {
          text: 'Write a sentence with "each of the participants"',
          example: 'Each of the participants was given a certificate.',
          tip: '"Each" is always singular—use "was" not "were".',
        },
        {
          text: 'Write a sentence with "the majority of students"',
          example: 'The majority of students are in favor of the proposal.',
          tip: 'When "majority of" + plural noun, use plural verb.',
        },
        {
          text: 'Write a sentence with "not only... but also"',
          example: 'Not only the manager but also the employees were surprised.',
          tip: 'Verb agrees with the subject after "but also".',
        },
      ],
    },
  ],
  tips: [
    'Always identify the true subject before choosing the verb',
    'Memorize which nouns are always singular (news, information, advice)',
    'Practice with compound subjects and "or/nor" constructions',
    'Pay attention to collective nouns in context',
  ],
  dailyPractice: [
    'Read news articles and identify subject-verb pairs',
    'Write sentences with tricky subjects',
    'Check your writing specifically for agreement errors',
    'Practice with IELTS-style sentences',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Subject-Verb Agreement: Ensuring subjects and verbs match in number, including tricky cases like collective nouns, compound subjects, and words between subject and verb.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...subjectVerbAgreementData,
      title: subjectVerbAgreementData.title + ' (Foundations)',
      overview: {
        ...subjectVerbAgreementData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: subjectVerbAgreementData.sections.slice(0, Math.max(2, Math.ceil(subjectVerbAgreementData.sections.length / 2))),
      exercises: subjectVerbAgreementData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...subjectVerbAgreementData,
      title: subjectVerbAgreementData.title + ' (Advanced)',
      overview: {
        ...subjectVerbAgreementData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: subjectVerbAgreementData.sections,
      exercises: subjectVerbAgreementData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(subjectVerbAgreementData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return subjectVerbAgreementData;
}
